import { useState } from "react";
import { api } from "@/api";
import { useSite } from "@/lib/site";
import { Send, Loader2, CheckCircle2 } from "lucide-react";

export default function InquiryForm({ variant = "light", productId = "", productName = "", type = "contact", title, subtitle }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const { settings } = useSite();

  const dark = variant === "dark";

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.message) {
      setError("Please fill name, email and message.");
      return;
    }
    setLoading(true);
    try {
      const recaptcha_token = window.__rcToken || null;
      await api.post("/enquiries", {
        ...form,
        type,
        product_id: productId || null,
        product_name: productName,
        source_url: typeof window !== "undefined" ? window.location.href : "",
        recaptcha_token,
      });
      setDone(true);
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className={`p-10 text-center rounded-sm ${dark ? "bg-[#1C1C1C] border border-[#333] text-white" : "bg-white border border-[#E5E7EB] text-[#1C1C1C]"}`} data-testid="enquiry-success">
        <CheckCircle2 size={48} className="text-[#C1121F] mx-auto mb-4" />
        <h3 className="h-card mb-2">Enquiry received.</h3>
        <p className={`font-body ${dark ? "text-white/70" : "text-[#4B5563]"}`}>
          Our team will reach out within 1 business day. For urgent enquiries, call {settings?.contact_phone}.
        </p>
        <button onClick={() => setDone(false)} className="mt-6 btn-outline-dark" data-testid="enquiry-send-another">
          Send another enquiry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={`p-8 md:p-10 rounded-sm ${dark ? "bg-[#1C1C1C] border border-[#333]" : "bg-white border border-[#E5E7EB]"}`} data-testid="enquiry-form">
      {(title || subtitle) && (
        <div className="mb-8">
          {title && <h3 className={`h-card ${dark ? "text-white" : "text-[#1C1C1C]"}`}>{title}</h3>}
          {subtitle && <p className={`mt-2 font-body ${dark ? "text-white/60" : "text-[#4B5563]"}`}>{subtitle}</p>}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field dark={dark} label="Full Name *" value={form.name} onChange={update("name")} testid="enquiry-name" />
        <Field dark={dark} label="Email *" type="email" value={form.email} onChange={update("email")} testid="enquiry-email" />
        <Field dark={dark} label="Phone" value={form.phone} onChange={update("phone")} testid="enquiry-phone" />
        <Field dark={dark} label="Company" value={form.company} onChange={update("company")} testid="enquiry-company" />
      </div>
      <div className="mt-4">
        <label className={`block text-xs uppercase tracking-wider font-heading font-semibold mb-2 ${dark ? "text-white/60" : "text-[#4B5563]"}`}>
          Message *
        </label>
        <textarea
          rows={5}
          value={form.message}
          onChange={update("message")}
          placeholder={productName ? `I'm interested in ${productName}…` : "Tell us about your requirement, quantity, application…"}
          className={dark ? "input-dark" : "input-light"}
          data-testid="enquiry-message"
        />
      </div>
      {productName && (
        <div className={`mt-3 text-xs font-body ${dark ? "text-white/50" : "text-[#4B5563]"}`}>
          Enquiry for: <span className={dark ? "text-white" : "text-[#1C1C1C]"}>{productName}</span>
        </div>
      )}
      {error && <p className="mt-4 text-sm text-[#C1121F] font-body" data-testid="enquiry-error">{error}</p>}
      <div className="mt-6 flex items-center justify-between">
        <p className={`text-xs font-body ${dark ? "text-white/40" : "text-[#4B5563]"}`}>
          We respect your privacy. No spam, ever.
        </p>
        <button type="submit" disabled={loading} className="btn-primary disabled:opacity-50" data-testid="enquiry-submit">
          {loading ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <>Send Enquiry <Send size={16} /></>}
        </button>
      </div>
    </form>
  );
}

function Field({ dark, label, type = "text", value, onChange, testid }) {
  return (
    <div>
      <label className={`block text-xs uppercase tracking-wider font-heading font-semibold mb-2 ${dark ? "text-white/60" : "text-[#4B5563]"}`}>
        {label}
      </label>
      <input type={type} value={value} onChange={onChange} className={dark ? "input-dark" : "input-light"} data-testid={testid} />
    </div>
  );
}
